import { Menu, Transition } from '@headlessui/react';
import { Mixpanel } from '../services/Mixpanel.js';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { Link, matchPath, useLocation } from 'react-router-dom';
import {
  daysLeftInTrial,
  isFreeTrialUser,
  isActiveIndividualSubscription,
  hasActiveSubscription,
  isPremiumUser,
  isInPremiumOrg,
  isInPremiumDomain,
} from '../utils/isPremiumUser';
import Image from './Image';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useAuth, useUser } from '@clerk/react';
import { DiffitUserContext } from '../contexts/DiffitUserContext.jsx';
import { VIEW_AS_TEACHER_PARAM } from '../utils/viewAsTeacher';
import { SIGN_OUT_TRIGGERS } from '../utils/signOutTriggers';
import CustomerIOPlaceholder from './CustomerIOPlaceholder.jsx';
import PremiumIconCrown from './PremiumIconCrown.jsx';
import crownIcon from '/public/premium_icon_crown.svg';
import { ChevronRightIcon, PencilIcon } from '@heroicons/react/24/outline';
import { Badge } from './Badge';
import BuildInfoModal from './modals/BuildInfoModal.jsx';
import ChangeProfilePictureModal from './modals/ChangeProfilePictureModal';
import ConnectedAccountsModal from './modals/ConnectedAccountsModal';
import { getEvalAccess } from '../services/EvalService';
import { getMyDistricts } from '../services/DistrictService';
import { useDebugMode } from '../hooks/useDebugMode';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { canAccessReferralPage } from '../utils/referralProgramConfig';

const PROVIDER_NAMES = { canva: 'Canva' };

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

// The resource currently on screen, read off the URL rather than context: this menu
// lives in the app shell, outside ProjectProvider. Both project routes rewrite the
// path to carry the active packet id (ProjectContext's first-packet redirect and
// switchToPacket), so the path is a reliable source once a resource has loaded.
const PACKET_ROUTE_PATTERNS = ['/project/:projectId/:packetId', '/p/:packetId'];

function useViewedPacketId() {
  const { pathname } = useLocation();
  for (const pattern of PACKET_ROUTE_PATTERNS) {
    const packetId = matchPath(pattern, pathname)?.params?.packetId;
    if (packetId) return packetId;
  }
  return null;
}

// Route items render as <Link> so cmd/ctrl/shift-click opens a new tab. Link
// runs Headless UI's merged onClick (ours + Menu.Item's CloseMenu) before its
// own preventDefault + navigate, so the menu still closes on a plain click.
export const MenuItem = ({ route, event, onClick, children, href }) => {
  const Tag = href ? 'a' : route ? Link : 'button';
  const linkProps = href
    ? { href, target: '_blank', rel: 'noopener noreferrer' }
    : route
      ? { to: route }
      : { type: 'button' };

  return (
    <Menu.Item>
      {({ active }) => (
        <Tag
          {...linkProps}
          onClick={() => {
            if (onClick) {
              onClick();
            }
            if (event) {
              Mixpanel.track(event.name ?? event, event.properties ?? {});
            }
          }}
          className={classNames(
            active && 'bg-gray-100',
            'w-full text-left block px-3 py-1.5 text-sm text-gray-700 rounded-md'
          )}>
          {children}
        </Tag>
      )}
    </Menu.Item>
  );
};

export const ProfileIconMenu = ({ sidebarExpanded = false } = {}) => {
  const isOnPricingPage = useLocation().pathname.includes('/pricing');
  const viewedPacketId = useViewedPacketId();
  const [debugMode, setDebugMode] = useDebugMode();
  const { user } = useUser();
  const {
    diffitUser,
    selectedOrg,
    setShowEditProfileModal,
    showPendingNotification,
    signOut,
    isLDidentified,
    isViewingAsTeacher,
    refreshDiffitUser,
  } = useContext(DiffitUserContext);

  const { isSignedIn, isLoaded } = useAuth();
  const { referralProgramConfiguration } = useFlags();
  const [showBuildInfoModal, setShowBuildInfoModal] = useState(false);
  const [showChangePhotoModal, setShowChangePhotoModal] = useState(false);
  const [isEvalContractor, setIsEvalContractor] = useState(false);
  const [adminOfDistricts, setAdminOfDistricts] = useState([]);
  const [showConnectedAccounts, setShowConnectedAccounts] = useState(false);
  // Arrives with the user (GET /user), so the entry is right on first render and never pops in.
  const hasConnectedAccounts = !!diffitUser?.connected_accounts?.length;

  useEffect(() => {
    if (isSignedIn && diffitUser && !diffitUser.is_diffit_admin) {
      getEvalAccess()
        .then((res) => {
          setIsEvalContractor(res.data?.is_eval_contractor ?? false);
        })
        .catch(() => {
          setIsEvalContractor(false);
        });
    }
  }, [isSignedIn, diffitUser]);

  useEffect(() => {
    if (isSignedIn && diffitUser) {
      getMyDistricts()
        .then((res) => setAdminOfDistricts(res.data?.districts ?? []))
        .catch(() => setAdminOfDistricts([]));
    }
  }, [isSignedIn, diffitUser]);

  const isSidebarCollapsed = !sidebarExpanded;

  if (!isSignedIn || !isLoaded || !isLDidentified) {
    return null;
  }

  const getPremiumToolTipText = () => {
    if (isInPremiumOrg(diffitUser) || isActiveIndividualSubscription(diffitUser)) {
      return 'You have unlimited access to Diffit';
    }
    return null;
  };

  const planLabel =
    isInPremiumOrg(diffitUser) || isInPremiumDomain(diffitUser)
      ? 'Diffit for Schools'
      : isActiveIndividualSubscription(diffitUser)
        ? 'Individual Plan'
        : isFreeTrialUser(user, diffitUser)
          ? 'Premium Trial'
          : null;

  return (
    <>
      {showBuildInfoModal && (
        <BuildInfoModal open={showBuildInfoModal} setOpen={setShowBuildInfoModal} />
      )}
      <ConnectedAccountsModal
        showModal={showConnectedAccounts}
        setShowModal={setShowConnectedAccounts}
        onDisconnected={() => refreshDiffitUser?.()}
      />
      {showChangePhotoModal && (
        <ChangeProfilePictureModal
          showModal={showChangePhotoModal}
          setShowModal={setShowChangePhotoModal}
        />
      )}
      <div className='w-full'>
        {!isOnPricingPage && (
          <div className='w-full'>
            <Menu as='div' className='relative'>
              {({ open, close }) => (
                <>
                  <div className='w-full'>
                    <Menu.Button
                      data-tutorial-id='profile-avatar-menu'
                      onClick={() => {
                        Mixpanel.track(`${open ? 'Close' : 'Open'} user menu`);
                      }}
                      className={classNames(
                        'flex focus:outline-none',
                        'items-center gap-x-3 w-full rounded-lg px-2 transition-colors hover:bg-gray-200',
                        sidebarExpanded && 'py-1.5 text-sm',
                        isSidebarCollapsed && 'py-2'
                      )}>
                      <span className='sr-only'>Open user menu</span>
                      <span className='relative flex-shrink-0'>
                        <Image
                          className={`rounded-full ${
                            isSidebarCollapsed
                              ? 'h-5 w-5 min-h-5 min-w-5'
                              : 'h-7 w-7 min-h-7 min-w-7'
                          }`}
                          src={user.imageUrl}
                          referrerPolicy='no-referrer'
                          alt=''
                        />
                        {isPremiumUser(user, diffitUser) && (
                          <span
                            className={`absolute ${
                              isSidebarCollapsed ? '-top-0.5 -right-0.5' : '-top-1 -right-1'
                            }`}
                            style={{
                              filter:
                                'drop-shadow(1px 0 0 #f3f4f6) drop-shadow(-1px 0 0 #f3f4f6) drop-shadow(0 1px 0 #f3f4f6) drop-shadow(0 -1px 0 #f3f4f6)',
                            }}>
                            <Image
                              src={crownIcon}
                              alt='Premium'
                              className={`block ${isSidebarCollapsed ? 'h-2 w-2' : 'h-3 w-3'}`}
                            />
                          </span>
                        )}
                      </span>
                      {sidebarExpanded && (
                        <div className='flex flex-col min-w-0 text-left'>
                          <span className='text-sm text-gray-600 truncate'>{user.fullName}</span>
                          {planLabel && (
                            <span className='text-xs text-gray-600 truncate'>{planLabel}</span>
                          )}
                        </div>
                      )}
                    </Menu.Button>
                    {showPendingNotification && !open && (
                      <div className='absolute -top-2 -right-2 h-5 w-5 rounded-full bg-red-500 flex items-center justify-center'>
                        <p className='text-white text-xs text-center'>1</p>
                      </div>
                    )}
                  </div>
                  <Transition
                    as={Fragment}
                    enter='transition ease-out duration-150'
                    enterFrom='opacity-0'
                    enterTo='opacity-100'
                    leave='transition ease-in duration-75'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0'>
                    <Menu.Items className='absolute z-50 rounded-lg bg-white py-1.5 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none w-60 bottom-full left-0 mb-2 origin-bottom-left'>
                      {/* Header: name, email, plan info */}
                      <div className='px-3 pt-2 pb-2 mx-1'>
                        <div className='flex items-center gap-2'>
                          <button
                            type='button'
                            onClick={() => {
                              close();
                              Mixpanel.track('Avatar Menu - Change Profile Picture');
                              setShowChangePhotoModal(true);
                            }}
                            className='group relative flex-shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2'
                            aria-label='Change profile picture'>
                            <Image
                              className='size-8 rounded-full'
                              src={user.imageUrl}
                              referrerPolicy='no-referrer'
                              alt=''
                            />
                            <div className='absolute inset-0 flex items-center justify-center rounded-full bg-black bg-opacity-40 opacity-0 transition-opacity group-hover:opacity-100'>
                              <PencilIcon className='h-4 w-4 text-white' />
                            </div>
                          </button>
                          <div className='min-w-0 flex-1'>
                            <div className='flex items-center'>
                              <span className='text-sm font-medium text-gray-700 truncate'>
                                {user.fullName}
                              </span>
                              {isPremiumUser(user, diffitUser) && (
                                <div className='inline-flex ml-auto font-normal w-4 h-4 shrink-0 -mt-1.5'>
                                  <PremiumIconCrown
                                    toolTipProps={{
                                      text: getPremiumToolTipText(),
                                      position: 'bottom',
                                      anchor: 'right',
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                            <div className='text-xs text-gray-500 truncate'>
                              {user.primaryEmailAddress.emailAddress}
                            </div>
                          </div>
                        </div>
                        {diffitUser && (
                          <a
                            href={
                              isActiveIndividualSubscription(diffitUser)
                                ? 'https://web.diffit.me/individual-teacher-subscription'
                                : 'https://web.diffit.me/plans'
                            }
                            target='_blank'
                            className='-mx-3 mt-1.5 flex items-center px-3 py-1.5 text-xs rounded-md hover:bg-gray-100 cursor-pointer'
                            onClick={() => {
                              Mixpanel.track('See plans clicked', {
                                location: 'Avatar Menu',
                                isFreeTrialUser: isFreeTrialUser(user, diffitUser),
                                daysLeftInTrial: daysLeftInTrial(user, diffitUser),
                              });
                            }}>
                            <span className='flex-1 min-w-0 text-gray-500'>
                              {hasActiveSubscription(diffitUser) ? (
                                'You have premium access to Diffit.'
                              ) : isFreeTrialUser(user, diffitUser) ? (
                                <>
                                  Your premium trial ends{' '}
                                  <span className='font-semibold text-gray-700 whitespace-nowrap'>
                                    {daysLeftInTrial(user, diffitUser) === 0
                                      ? 'today'
                                      : daysLeftInTrial(user, diffitUser) === 1
                                        ? 'in 1 day'
                                        : 'in ' + daysLeftInTrial(user, diffitUser) + ' days'}
                                  </span>
                                  .
                                </>
                              ) : (
                                "You are using Diffit's free version."
                              )}{' '}
                              <span className='text-amber-700 font-medium whitespace-nowrap'>
                                {hasActiveSubscription(diffitUser) ? 'See benefits' : 'See Plans'}
                                <ChevronRightIcon className='inline-block h-3 w-3 align-text-bottom' />
                              </span>
                            </span>
                          </a>
                        )}
                        {!!diffitUser && diffitUser.active_orgs?.length >= 1 && selectedOrg && (
                          <div className='mt-1 text-sm text-emerald-600'>
                            <a
                              className={`${
                                selectedOrg.role === 'Admin'
                                  ? 'hover:text-emerald-400'
                                  : 'pointer-events-none'
                              }`}
                              href={`${selectedOrg.role === 'Admin' ? `/org/${selectedOrg.org_id}` : ''}`}>
                              {selectedOrg.org.name}
                            </a>
                          </div>
                        )}
                        {!!diffitUser &&
                          !diffitUser.active_orgs?.length >= 1 &&
                          !selectedOrg &&
                          isInPremiumDomain(diffitUser) && (
                            <div className='mt-1 text-sm text-emerald-600'>
                              {diffitUser.premium_domain.school_record.name}
                            </div>
                          )}
                        {diffitUser?.badges_json?.length > 0 && (
                          <div className='flex flex-col gap-2 mt-2'>
                            <div className='text-xs font-medium text-gray-500'>My Badges</div>
                            {/* Wraps because six badges (6x32 + 5x8 = 232px)
                                exceed the 208px this w-60 panel leaves after
                                px-3 and mx-1, and a Badge can't shrink below
                                its w-8. */}
                            <div className='flex flex-row flex-wrap justify-start gap-2'>
                              {diffitUser.badges_json.map((badge) => (
                                <Badge key={badge.type} badge={badge} />
                              ))}
                            </div>
                          </div>
                        )}
                        <CustomerIOPlaceholder id='customerio-profile-menu-item' instant={true} />
                      </div>

                      {/* Navigation group */}
                      <div className='border-t border-gray-100 py-1 mx-1'>
                        <MenuItem route='/resources' event='My Resources'>
                          My Resources
                        </MenuItem>
                        {/* Same rule as ReferralRouteGate, so the link never offers a page the
                            gate redirects away from. Fails closed while flags are in flight; the
                            item appears once they land. */}
                        {canAccessReferralPage(diffitUser, referralProgramConfiguration) && (
                          <MenuItem route='/referral' event='Share Diffit Menu Clicked'>
                            Share Diffit
                          </MenuItem>
                        )}
                        {/* A District Admin reaches the district's schools from the District
                            Dashboard, so one link, not two. */}
                        {selectedOrg &&
                          selectedOrg.role === 'Admin' &&
                          !adminOfDistricts.some((d) => d.id === selectedOrg.org.district_id) && (
                            <MenuItem route={'/org/' + selectedOrg.org.id} event='Admin Dashboard'>
                              Manage Users
                            </MenuItem>
                          )}
                        {adminOfDistricts.map((district) => (
                          <MenuItem
                            key={district.id}
                            route={`/district/${district.id}`}
                            event='District Dashboard'>
                            {adminOfDistricts.length > 1 ? district.name : 'District Dashboard'}
                          </MenuItem>
                        ))}
                        {!!diffitUser &&
                          (isActiveIndividualSubscription(diffitUser) ||
                            diffitUser.stripe_needs_attention) && (
                            <MenuItem route='/subscription' event='Manage Subscription'>
                              Manage Subscription{' '}
                              {diffitUser.stripe_needs_attention && (
                                <ExclamationCircleIcon className='h-5 w-5 self-center text-red-500 inline' />
                              )}
                            </MenuItem>
                          )}
                        {/* Keyed on the context flag, not on is_diffit_admin —
                            that one is masked while this is on, so the way out
                            has to be the one item that does not depend on it. */}
                        {isViewingAsTeacher && (
                          <MenuItem
                            event='Exited Teacher View'
                            route={{ search: `?${VIEW_AS_TEACHER_PARAM}=0` }}>
                            Exit teacher view
                          </MenuItem>
                        )}
                        {!!diffitUser && diffitUser.is_diffit_admin && (
                          <>
                            <MenuItem route='/admin'>Diffit Admin</MenuItem>
                            <MenuItem
                              event='Build Info Modal Opened'
                              onClick={() => setShowBuildInfoModal(true)}>
                              Build Info
                            </MenuItem>
                            <MenuItem onClick={() => setDebugMode(!debugMode)}>
                              {debugMode ? 'Turn off debug mode' : 'Turn on debug mode'}
                            </MenuItem>
                          </>
                        )}
                        {(diffitUser?.is_diffit_admin || isEvalContractor) && (
                          <MenuItem route='/eval' event='Eval Dashboard Opened'>
                            Eval Dashboard
                          </MenuItem>
                        )}
                        {/* Lives here rather than on the resource itself so the
                            packet page stays byte-identical to what a teacher sees —
                            admins have to be able to evaluate that experience. */}
                        {!!diffitUser?.is_diffit_admin && !!viewedPacketId && (
                          <MenuItem
                            href={`/eval?tab=review&packet=${viewedPacketId}`}
                            event={{
                              name: 'Admin Eval Link Clicked',
                              properties: { packet_id: viewedPacketId },
                            }}>
                            Eval This Resource
                          </MenuItem>
                        )}
                      </div>

                      {/* Account group */}
                      <div className='border-t border-gray-100 py-1 mx-1'>
                        {!!diffitUser && (
                          <MenuItem
                            onClick={() => setShowEditProfileModal(true)}
                            event='Avatar Menu - Selected Profile'>
                            <div className='flex items-center'>
                              Update Profile
                              {showPendingNotification && open && (
                                <div className='ml-2 h-5 w-5 rounded-full bg-red-500 flex items-center justify-center'>
                                  <p className='text-white text-xs text-center'>1</p>
                                </div>
                              )}
                            </div>
                            <div className='text-xs text-gray-500'>Personalize your experience</div>
                          </MenuItem>
                        )}
                        {hasConnectedAccounts && (
                          <MenuItem
                            onClick={() => setShowConnectedAccounts(true)}
                            event='Avatar Menu - Connected Accounts'>
                            Connected Accounts
                            <div className='text-xs text-gray-500'>
                              {diffitUser.connected_accounts
                                .map((provider) => PROVIDER_NAMES[provider] ?? provider)
                                .join(', ')}
                            </div>
                          </MenuItem>
                        )}

                        <MenuItem onClick={() => signOut(SIGN_OUT_TRIGGERS.USER_MENU)}>
                          Sign out
                        </MenuItem>
                      </div>
                    </Menu.Items>
                  </Transition>
                </>
              )}
            </Menu>
          </div>
        )}
      </div>
    </>
  );
};
